import * as XLSX from "xlsx";

/* =========================================================
   PARSE & NORMALIZE EXCEL ROW DATA
   ========================================================= */
export const normalizeStudentNameFromRow = (row: any): string => {
  if (!row) return "";
  return (
    row["Name"] ??
    row["Student Name"] ??
    row["StudentName"] ??
    row["student_name"] ??
    row["studentName"] ??
    row["Physical Measurements"] ??
    row["Physical Measurements Report"] ??
    row["name"] ??
    row["fullName"] ??
    row["full_name"] ??
    ""
  ).toString().trim();
};

export const parseExcelMeasurementDate = (row: any): string => {
  if (!row) return new Date().toISOString();
  const rawDate =
    row["MeasurementDate"] ??
    row["Measurement Date"] ??
    row["measurement_date"] ??
    row["measurementDate"] ??
    row["Last Measurement"] ??
    row["last_measurement"] ??
    row["lastMeasurement"] ??
    row["Date"] ??
    row["date"] ??
    "";

  if (!rawDate) return new Date().toISOString();

  // If XLSX parsed it as an Excel numeric serial date (e.g. 45545)
  if (typeof rawDate === "number" && rawDate > 30000 && rawDate < 60000) {
    const jsDate = new Date(Math.round((rawDate - 25569) * 86400 * 1000));
    return isNaN(jsDate.getTime()) ? new Date().toISOString() : jsDate.toISOString();
  }

  const d = new Date(rawDate);
  if (!isNaN(d.getTime())) {
    return d.toISOString();
  }
  return String(rawDate);
};

/* =========================================================
   SAFE CELL VALUE FORMATTER (Null / NaN / Undefined Guard)
   ========================================================= */
const safeCell = (val: any, fallback = ""): string | number => {
  if (val === null || val === undefined || val === "" || val === "-" || val === "N/A") {
    return fallback;
  }
  if (typeof val === "number") {
    if (isNaN(val) || !isFinite(val)) return fallback;
    return val;
  }
  const str = String(val).trim();
  if (str === "undefined" || str === "null" || str === "NaN") return fallback;
  return str;
};

/* =========================================================
   EXPORT ACTUAL MEASUREMENTS (PEDIATRIC-FRIENDLY HEADERS)
   ========================================================= */
export const exportExcel = (rows: any[], selectedStudentName?: string) => {
  const cleanedRows = rows.map((row, idx) => ({
    "S.No": idx + 1,
    "Student Name": safeCell(row.name),
    "Gender": safeCell(row.gender),
    "Age": safeCell(row.exactAgeYears > 0 ? `${row.exactAgeYears} years` : (row.age && row.age !== "-" ? `${row.age} years` : "")),
    "DOB": safeCell(row.dob),
    "Email": safeCell(row.email),
    "Mobile": safeCell(row.mobile),
    "Height (cm)": safeCell(row.height),
    "Weight (kg)": safeCell(row.weight),
    "BMI": safeCell(row.bmi),
    "WHO BMI-for-age Classification": safeCell(row.obesityLevel || row.obesity_level),
    "Reference Median Weight (P50)": safeCell(row.standardWeight || row.standard_weight),
    "Body Fat (%)": safeCell(row.bodyFat || row.body_fat),
    "Lean Body Mass (kg)": safeCell(row.leanBodyMass || row.lean_body_mass),
    "Muscle Mass (kg)": safeCell(row.muscleMass || row.muscle_mass),
    "Bone Mass (kg)": safeCell(row.boneMass || row.bone_mass),
    "BMR (kcal/day)": safeCell(row.bmr),
    "Protein (%)": safeCell(row.protein),
    "Body Moisture (%)": safeCell(row.bodyMoisture || row.body_moisture),
    "Visceral Index": safeCell(row.visceralFat || row.visceral_fat),
    "Subcutaneous Fat (%)": safeCell(row.subcutaneousFat || row.subcutaneous_fat),
    "Heart Rate (bpm)": safeCell(row.heartRate || row.heart_rate),
    "Body Type": safeCell(row.bodyType || row.body_type),
    "Measurement Date": safeCell(row.lastMeasurement || row.measurementDate || row.createdAt)
  }));

  const worksheet = XLSX.utils.json_to_sheet(cleanedRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Measurements");

  const fileName = selectedStudentName
    ? `${selectedStudentName.replace(/[^a-zA-Z0-9_-]/g, "_")}_Physical_Measurements.xlsx`
    : "Physical_Measurements_Report.xlsx";

  XLSX.writeFile(workbook, fileName);
};

/* =========================================================
   SAMPLE EXCEL FOR BULK UPLOAD (TEMPLATE)
   ========================================================= */
export const sampleExcel = () => {
  const sampleData = [
    {
      name: "John Doe",
      email: "johndoe@example.com",
      mobile: "9876543210",
      gender: "Male",
      dob: "2015-05-15",
      height: 140,
      weight: 35,
      heart_rate: 75,
      measurement_date: "2026-09-10"
    }
  ];

  const worksheet = XLSX.utils.json_to_sheet(sampleData);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, "Sample");
  XLSX.writeFile(workbook, "sample_physical_measurements.xlsx");
};
